import React, { useState, useMemo } from "react";
import {
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import {
  getErrorResponse,
  getHeader,
  roundToMaxDecimals,
} from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";

// ---------------------------------------------------------------------------
// Static metadata shared across the component
// ---------------------------------------------------------------------------
const SERVICE_META = {
  whatsapp: {
    label: "WhatsApp",
    walletField: "wallet",
    expiryField: "expiryDate",
    chipColor: "#2e7d32",
  },
  voice: {
    label: "Voice",
    walletField: "voiceCallWallet",
    expiryField: "voiceCallWalletExpiry",
    chipColor: "#1565c0",
  },
  rcs: {
    label: "RCS",
    walletField: "rcsWalletRichMedia",
    expiryField: "rcsWalletRichMediaExpiry",
    chipColor: "#6a1b9a",
  },
};

const COMBO_COLORS = {
  all_three: "#2e7d32",
  wa_voice: "#0088FE",
  wa_rcs: "#8884d8",
  voice_rcs: "#00C49F",
  wa_only: "#25D366",
  voice_only: "#1565c0",
  rcs_only: "#6a1b9a",
};

// Relative "last used" formatting -> { text, color } used for chips
const formatLastUsed = (dateStr) => {
  if (!dateStr)
    return {
      text: "Never used",
      color: "default",
      tooltip: "No usage recorded",
    };
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  const tooltip = new Date(dateStr).toLocaleString();
  if (diffDays <= 0) return { text: "Today", color: "success", tooltip };
  if (diffDays <= 7)
    return { text: `${diffDays}d ago`, color: "success", tooltip };
  if (diffDays <= 30)
    return { text: `${diffDays}d ago`, color: "warning", tooltip };
  return { text: `${diffDays}d ago`, color: "error", tooltip };
};

const expiryStatus = (expiryDate) => {
  if (!expiryDate) return null;
  const now = Date.now();
  const exp = new Date(expiryDate).getTime();
  const next7 = now + 7 * 24 * 60 * 60 * 1000;
  if (exp < now) return { text: "Expired", color: "error" };
  if (exp < next7) return { text: "Expiring Soon", color: "warning" };
  return { text: "Active", color: "success" };
};

// ---------------------------------------------------------------------------
// Sub-component: on-demand usage activity + last-used chips (prevents N+1 load)
// ---------------------------------------------------------------------------
const ActivityStatus = ({ customerId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const checkActivity = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_ENDPOINT}/root/get-usage-active-client?customerId=${customerId}`,
        getHeader()
      );
      setStatus(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress size={18} />;
  if (!status)
    return (
      <Button size="small" variant="outlined" onClick={checkActivity}>
        Check Usage
      </Button>
    );

  const rows = [
    { key: "whatsapp", label: "WA", data: status.whatsapp },
    { key: "voice", label: "Voice", data: status.voice },
    { key: "rcs", label: "RCS", data: status.rcs },
  ];

  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {rows.map(({ key, label, data }) => {
        const info = formatLastUsed(data?.lastUsed);
        return (
          <Tooltip key={key} title={info.tooltip} arrow placement="top">
            <Chip
              size="small"
              label={`${label}: ${info.text}`}
              color={info.color}
              variant={data?.active ? "filled" : "outlined"}
              sx={{ justifyContent: "flex-start" }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AccountsAnalytics = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/root/get-accounts-analytics`
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [listData, setListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  // currentView: { mode: 'service' | 'combo', services: [...], status, label }
  const [currentView, setCurrentView] = useState({
    mode: "service",
    services: [],
    status: "",
    label: "",
  });

  if (isLoading) return <MuPb />;
  if (isError) return <Alert severity="error">{getErrorResponse(error)}</Alert>;

  // ---- Bar chart (per-service health) ----
  const chartData = [
    {
      name: "WhatsApp",
      active: data.whatsapp.active,
      expiringSoon: data.whatsapp.expiringSoon,
      expired: data.whatsapp.expired,
    },
    {
      name: "Voice",
      active: data.voice.active,
      expiringSoon: data.voice.expiringSoon,
      expired: data.voice.expired,
    },
    {
      name: "RCS",
      active: data.rcs.active,
      expiringSoon: data.rcs.expiringSoon,
      expired: data.rcs.expired,
    },
  ];

  // ---- Combo data (from backend, always all 7 keys present) ----
  const combos = data.combos || [];
  const pieData = combos
    .filter((c) => c.count > 0)
    .map((c) => ({ name: c.label, value: c.count, key: c.key }));

  // ---- Fetch list for a single service + status (existing behaviour) ----
  const handleFetchList = async (service, status) => {
    setCurrentView({
      mode: "service",
      services: [service],
      status,
      label: SERVICE_META[service].label,
    });
    setModalOpen(true);
    setListLoading(true);
    setListError(null);
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/root/get-accounts-list?service=${service}&status=${status}`,
        getHeader()
      );
      setListData(response.data);
    } catch (err) {
      console.error("Failed to fetch account list", err);
      setListError("Failed to load accounts. Please try again.");
    } finally {
      setListLoading(false);
    }
  };

  // ---- Fetch list for an exact combination (all-three, wa+voice, wa-only, etc) ----
  const handleFetchCombo = async (comboKey, label) => {
    setCurrentView({ mode: "combo", services: [], status: null, label });
    setModalOpen(true);
    setListLoading(true);
    setListError(null);
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/root/get-accounts-list-by-combo?comboKey=${comboKey}`,
        getHeader()
      );
      setCurrentView({
        mode: "combo",
        services: response.data.combo.services,
        status: null,
        label,
      });
      setListData(response.data.accounts);
    } catch (err) {
      console.error("Failed to fetch combo list", err);
      setListError("Failed to load accounts. Please try again.");
    } finally {
      setListLoading(false);
    }
  };

  const handlePieClick = (entry) => {
    if (entry && entry.key) handleFetchCombo(entry.key, entry.name);
  };

  const StatCard = ({ title, stats, serviceKey }) => (
    <Card sx={{ height: "100%", mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Total: {stats.total}
        </Typography>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={() => handleFetchList(serviceKey, "active")}
          >
            Active: {stats.active}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => handleFetchList(serviceKey, "expiringSoon")}
          >
            Expiring (less than 7 days): {stats.expiringSoon}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleFetchList(serviceKey, "expired")}
          >
            Expired: {stats.expired}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // Columns to render in the modal table for the current view
  const activeServices =
    currentView.mode === "combo" ? currentView.services : currentView.services;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Subscriptions & Renewals
      </Typography>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <StatCard
            title="WhatsApp"
            stats={data.whatsapp}
            serviceKey="whatsapp"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard title="Voice" stats={data.voice} serviceKey="voice" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard title="RCS" stats={data.rcs} serviceKey="rcs" />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Health Overview
              </Typography>
              <Box sx={{ height: 350, width: "100%" }}>
                <ResponsiveContainer>
                  <BarChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Bar
                      dataKey="active"
                      stackId="a"
                      fill="#2e7d32"
                      name="Active"
                    />
                    <Bar
                      dataKey="expiringSoon"
                      stackId="a"
                      fill="#ed6c02"
                      name="Expiring Soon"
                    />
                    <Bar
                      dataKey="expired"
                      stackId="a"
                      fill="#d32f2f"
                      name="Expired"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Combinations
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Click a slice or a row below to see the customer list
              </Typography>
              <Box sx={{ height: 250, width: "100%" }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label
                      onClick={(entry) => handlePieClick(entry)}
                      cursor="pointer"
                    >
                      {pieData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COMBO_COLORS[entry.key] || "#999"}
                        />
                      ))}
                    </Pie>
                    <ChartTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Exact-combo breakdown list (all three / two-combos / single-service-only) */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  mt: 1,
                }}
              >
                {combos.map((c) => (
                  <CardActionArea
                    key={c.key}
                    onClick={() => handleFetchCombo(c.key, c.label)}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      border: "1px solid #eee",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: COMBO_COLORS[c.key] || "#999",
                        }}
                      />
                      <Typography variant="body2">{c.label}</Typography>
                    </Box>
                    <Chip size="small" label={c.count} />
                  </CardActionArea>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Modal - works for both single-service and combo views */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {currentView.label} Accounts
          {currentView.status
            ? ` — ${currentView.status.replace(/([A-Z])/g, " $1")}`
            : ""}
        </DialogTitle>
        <DialogContent dividers>
          {listLoading ? (
            <MuPb />
          ) : listError ? (
            <Alert severity="error">{listError}</Alert>
          ) : listData.length === 0 ? (
            <Typography>No accounts found for this criteria.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Plan</TableCell>
                  {activeServices.map((svc) => (
                    <TableCell key={svc}>
                      {SERVICE_META[svc].label} Expiry / Balance
                    </TableCell>
                  ))}
                  <TableCell>Usage Activity (last used)</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listData.map((user, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <strong>{user.customerId || "Unknown"}</strong>
                    </TableCell>
                    <TableCell>{user.subscriptionPlan || "Default"}</TableCell>
                    {activeServices.map((svc) => {
                      const meta = SERVICE_META[svc];
                      const expiry = user[meta.expiryField];
                      const balance = user[meta.walletField];
                      const status = expiryStatus(expiry);
                      return (
                        <TableCell key={svc}>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption">
                                {expiry
                                  ? new Date(expiry).toLocaleDateString()
                                  : "-"}
                              </Typography>
                              {status && (
                                <Chip
                                  size="small"
                                  label={status.text}
                                  color={status.color}
                                />
                              )}
                            </Box>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Bal: ${roundToMaxDecimals(
                                balance ?? 0,
                                2
                              )}`}
                            />
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      <ActivityStatus customerId={user.customerId} />
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="contained" color="primary">
                        Remind
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AccountsAnalytics;
